import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PrimaryButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  size?: "default" | "lg";
  isLoading?: boolean;
  /** Opt out of wrapping. Only for short, guaranteed-fitting labels. */
  nowrap?: boolean;
}

export const PrimaryButton = React.forwardRef<HTMLButtonElement, PrimaryButtonProps>(
  ({ className, asChild = false, size = "default", isLoading, nowrap = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const content =
      !asChild && isLoading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={3} aria-hidden />
          <span>{children}</span>
        </>
      ) : (
        children
      );
    return (
      <Comp
        ref={ref}
        disabled={!asChild ? disabled || isLoading : undefined}
        aria-busy={!asChild && isLoading ? true : undefined}
        className={cn(
          "inline-flex max-w-full items-center justify-center gap-2 text-center",
          nowrap ? "whitespace-nowrap" : "whitespace-normal break-words",
          "bg-bronze-600 text-navy-900 font-sans font-semibold uppercase",
          "tracking-[0.05em] transition-colors cursor-pointer",
          "hover:bg-bronze-400 focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-bronze-400 focus-visible:ring-offset-2",
          "focus-visible:ring-offset-navy-900 disabled:opacity-50 disabled:pointer-events-none",
          size === "lg" ? "min-h-12 px-7 py-3 text-sm" : "min-h-10 px-5 py-2 text-[13px]",
          className,
        )}
        style={{ borderRadius: "var(--radius-button)" }}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
PrimaryButton.displayName = "PrimaryButton";

export default PrimaryButton;